import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow, PageHero } from "@/components/Reveal";
import NewsletterForm from "@/components/NewsletterForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  useSeo(
    "Insights | Reform Intelligence®",
    "Research, frameworks, and perspective on independent AI evaluation and governance from Reform Intelligence."
  );

  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetch(`${API}/articles`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setPosts)
      .catch(() =>
        fetch("/insights/manifest.json")
          .then((r) => r.json())
          .then(setPosts)
          .catch(() => setPosts([]))
      );
  }, []);

  const categories = useMemo(() => ["All", ...new Set(posts.map((p) => p.category))], [posts]);
  const visible = filter === "All" ? posts : posts.filter((p) => p.category === filter);

  return (
    <>
      <PageHero
        eyebrow="Insights"
        headline="Research and perspective on trustworthy AI."
        subhead="Frameworks, analysis, and the standards we believe the field needs."
      />

      <section data-testid="insights-hub" className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setFilter(c)}
                  className={`border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                    filter === c
                      ? "border-[#1F2D4E] bg-[#1F2D4E] text-white"
                      : "border-[#E4E7EC] text-[#5A6B8C] hover:border-[#B08D3F] hover:text-[#B08D3F]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 space-y-6">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                {p.coming_soon ? (
                  <article data-testid={`insight-card-${p.slug}`} className="border border-[#E4E7EC] bg-[#F5F6F8] p-8">
                    <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.15em]">
                      <span className="text-[#B08D3F]">{p.category}</span>
                      <span className="text-[#98A0B3]">Coming soon</span>
                    </div>
                    <h2 className="mt-3 font-serif2 text-2xl font-semibold text-[#1F2D4E]">{p.title}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-[#5A6B8C]">{p.summary}</p>
                    <p className="mt-3 text-xs text-[#98A0B3]">{p.author}</p>
                  </article>
                ) : (
                  <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="block border border-[#E4E7EC] p-8 transition-all hover:-translate-y-0.5 hover:border-[#B08D3F]">
                    <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.15em]">
                      <span className="text-[#B08D3F]">{p.category}</span>
                      <span className="text-[#98A0B3]">{p.date}</span>
                    </div>
                    <h2 className="mt-3 font-serif2 text-2xl font-semibold text-[#1F2D4E]">{p.title}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-[#5A6B8C]">{p.summary}</p>
                    <p className="mt-3 text-xs text-[#98A0B3]">{p.author}</p>
                  </Link>
                )}
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 border border-[#E4E7EC] bg-[#F5F6F8] p-8 sm:p-10">
              <Eyebrow>Newsletter</Eyebrow>
              <p className="mt-3 font-serif2 text-xl font-semibold text-[#1F2D4E]">Get our research. Enter your email.</p>
              <div className="mt-5">
                <NewsletterForm testid="insights-newsletter" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
